import { useEffect, useRef, useState } from 'react';
import { Image, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { BACKEND_URL, adminAuthHeaders, adminMultipartHeaders, api } from '../../lib/api';

function MediaPickerDialog({ open, onOpenChange, onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    let isActive = true;
    const fetchMedia = async () => {
      try {
        setLoading(true);
        const response = await api.get('/admin/media', adminAuthHeaders());
        if (!isActive) return;
        setItems(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        if (!isActive) return;
        console.error('Failed to load media library:', error);
        toast.error('Failed to load media library');
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchMedia();

    return () => {
      isActive = false;
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-zinc-950 text-white sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Select media</DialogTitle>
          <DialogDescription className="text-white/60">
            Choose an existing uploaded image from the media library.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-white/50">Loading media...</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-white/50">Media library is empty.</div>
        ) : (
          <div className="grid max-h-[60vh] grid-cols-2 gap-4 overflow-y-auto md:grid-cols-4">
            {items.map((item) => {
              const src = item.url?.startsWith('http') ? item.url : `${BACKEND_URL}${item.url}`;
              return (
                <button
                  key={item.id || item.url}
                  type="button"
                  onClick={() => {
                    onSelect(item.url);
                    onOpenChange(false);
                  }}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 text-left transition-colors hover:border-[#E11D2E] hover:bg-white/10"
                >
                  <div className="aspect-[4/3] bg-black/30">
                    <img src={src} alt={item.original_name || 'media'} className="h-full w-full object-cover" />
                  </div>
                  <div className="space-y-1 p-3">
                    <div className="truncate text-sm text-white">{item.original_name || item.url}</div>
                    <div className="truncate text-xs text-white/40">{item.url}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ImageInput({ value, onChange, placeholder, className }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/media', formData, adminMultipartHeaders());
      onChange(res.data.url);
      toast.success('Uploaded');
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const imgSrc = value
    ? value.startsWith('http') ? value : `${BACKEND_URL}${value}`
    : null;

  return (
    <div className="space-y-1">
      <div className="flex gap-2 items-center">
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={className || 'bg-zinc-900/50 border-white/10 text-white'}
          placeholder={placeholder || 'Image URL'}
        />
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="bg-emerald-600/30 hover:bg-emerald-600/50 text-emerald-300 rounded-lg shrink-0 px-3"
          disabled={uploading}
        >
          {uploading ? <span className="animate-spin text-sm">⏳</span> : <Upload className="w-4 h-4" />}
        </Button>
        <Button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="bg-white/10 hover:bg-white/20 text-white rounded-lg shrink-0 px-3"
        >
          <Image className="w-4 h-4" />
        </Button>
      </div>
      {imgSrc && (
        <div className="flex items-center gap-2">
          <img
            src={imgSrc}
            className="h-10 w-16 rounded object-cover border border-white/10"
            alt=""
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="text-white/30 text-xs truncate max-w-[200px]">{value}</span>
        </div>
      )}
      <MediaPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onSelect={onChange} />
    </div>
  );
}

export function GalleryInput({ images, onChange, className }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const newUrls = [];
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/admin/media', formData, adminMultipartHeaders());
        newUrls.push(res.data.url);
      }
      onChange([...(images || []), ...newUrls]);
      toast.success(`Uploaded ${newUrls.length} image(s)`);
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeImage = (idx) => {
    onChange((images || []).filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <Textarea
          value={(images || []).join(', ')}
          onChange={(e) => onChange(e.target.value.split(',').map((item) => item.trim()).filter(Boolean))}
          className={className || 'bg-zinc-900/50 border-white/10 text-white'}
          placeholder="Image URLs (comma separated)"
          rows={2}
        />
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="bg-emerald-600/30 hover:bg-emerald-600/50 text-emerald-300 rounded-lg shrink-0 px-3 self-start mt-1"
          disabled={uploading}
        >
          {uploading ? <span className="animate-spin text-sm">⏳</span> : <Upload className="w-4 h-4" />}
        </Button>
        <Button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="bg-white/10 hover:bg-white/20 text-white rounded-lg shrink-0 px-3 self-start mt-1"
        >
          <Image className="w-4 h-4" />
        </Button>
      </div>
      {(images || []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {(images || []).map((img, idx) => {
            const src = img.startsWith('http') ? img : `${BACKEND_URL}${img}`;
            return (
              <div key={`${img}-${idx}`} className="relative group">
                <img src={src} className="h-16 w-20 rounded object-cover border border-white/10" alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(url) => onChange([...(images || []), url])}
      />
    </div>
  );
}
